import React, { useState, useContext, createContext } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from '../styles/PantallaDeProgramasAcademicos.v2.module.css';
import logoStyles from './Sidebar.module.css';
import { useAuth, hasAdminRole, hasStudentRole } from '../api/AuthContext';

const SidebarContext = createContext();

export function useSidebar() {
  return useContext(SidebarContext);
}

const SidebarProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleSidebar = () => setIsCollapsed((v) => !v);
  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar, setIsCollapsed }}>
      {children}
    </SidebarContext.Provider>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { user } = useAuth();
  const isActive = (path) => location.pathname.startsWith(path);
  const [open, setOpen] = useState({ 
    consulta: false,
    finanzas: false,
    controlEscolar: false, 
    perfilAcademico: false, 
    adminSistema: false,
    catalogos: false,
    reportes: false,
    admisiones: false,
    panelAsesor: false,
    reportesPerfilAcademico: false,
    reportesControlEscolar: false,
  });
  const isSysUser = !!(user && (user.isSysUser || user.id_usuario || user.id_rol));
  const isStudent = hasStudentRole(user);
  const isAdmin = hasAdminRole(user);

  // No mostrar el sidebar para estudiantes ni para usuarios sin perfil sys_usuarios
  if (!isSysUser || isStudent) {
    return null;
  }
  const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  // Funciones para expandir y colapsar todos los menús
  const expandAll = () => setOpen((prev) => {
    const expanded = {};
    Object.keys(prev).forEach(k => expanded[k] = true);
    return expanded;
  });
  const collapseAll = () => setOpen((prev) => {
    const collapsed = {};
    Object.keys(prev).forEach(k => collapsed[k] = false);
    return collapsed;
  });

  // Determinar si todo está expandido o colapsado
  const allExpanded = Object.values(open).every(Boolean);
  const allCollapsed = Object.values(open).every(v => !v);
  const handleToggleAll = () => {
    if (allExpanded) {
      // Colapsar todo
      setOpen(prev => {
        const collapsed = {};
        Object.keys(prev).forEach(k => collapsed[k] = false);
        return collapsed;
      });
    } else {
      // Expandir todo
      setOpen(prev => {
        const expanded = {};
        Object.keys(prev).forEach(k => expanded[k] = true);
        return expanded;
      });
    }
  };

  const sections = [
    {
      key: 'consulta',
      title: 'CONSULTA',
      permiso: 'Consulta',
      items: [
        { label: 'Estudiantes Consulta', path: '/consulta/estudiantes', icon: '🔎', long: true },
      ],
    },
    {
      key: 'finanzas',
      title: 'FINANZAS',
      permiso: 'Finanzas',
      items: [
        { label: 'Inscripción', path: '/inscripcion-nuevo', icon: '📋', long: false },
        { label: 'Reinscripción', path: '/inscripcion-reinscripcion', icon: '📝', long: false },
        { label: 'Asignación de Becas', path: '/asignacion-becas', icon: '🎓', long: true },
        { label: 'Pagos', path: '/generacion-cargos-manuales', icon: '💳', long: false },
        { label: 'Cargos Automáticos', path: '/generacion-cargos-automaticos', icon: '⚡', long: true },
      ],
      subgroups: [
        {
          key: 'reportesFinancieros',
          title: 'Reportes Financieros',
          permiso: 'Finanzas',
          items: [
            { label: 'Reporte de Ingresos Diarios', path: '/reporte-ingresos-diarios', icon: '💰', long: true },
            { label: 'Reporte de Adeudos', path: '/reporte-adeudos', icon: '📉', long: true },
            { label: 'Reporte Seguros Estudiantes', path: '/reporte-seguros-estudiantes', icon: '🛡️', long: true },
            { label: 'Reporte Seguros Asesores', path: '/reporte-seguros-asesores', icon: '🛡️', long: true },
            { label: 'Reporte de Presupuesto por Periodo', path: '/reporte-rectoria', icon: '🏛️', long: true },
            { label: 'Becas', path: '/reporte-becas', icon: '🎓', long: true },
          ],
        },
      ],
    },
    {
      key: 'controlEscolar',
      title: 'CONTROL ESCOLAR',
      permiso: 'Control Escolar',
      items: [
        { label: 'Programas Académicos', path: '/programas', icon: '📝', long: false },
        { label: 'Materias', path: '/materias', icon: '📕', long: false },
        { label: 'Asignación de Programa-Materia', path: '/asignacion-programa-materia', icon: '✔️', long: true },
        { label: 'Calificaciones Escolar', path: '/calificaciones-escolar', icon: '📊', long: true },
        { label: 'Estudiantes', path: '/estudiantes', icon: '👨‍🎓', long: false },
        { label: 'Constancias', path: '/constancias', icon: '📜', long: false },
        { label: 'Materias Equivalentes a Revalidar', path: '/materias-equivalentes-revalidar', icon: '🔁', long: true },
        { label: 'Baja/Reactivación', path: '/baja-reactivacion', icon: '⏸️', long: true },
        { label: 'Trámites Escolares', path: '/tramites-escolares', icon: '📄', long: true },
      ],
      subgroups: [
        {
          key: 'reportesControlEscolar',
          title: 'Reportes',
          permiso: 'Control Escolar',
          items: [
            { label: 'Reporte de Eficiencia Terminal', path: '/reporte-eficiencia-terminal', icon: '🎓', long: true },
            { label: 'Reporte de estudiantes inscritos', path: '/reporte-estudiantes-inscritos', icon: '🧑‍🎓', long: true },
            { label: 'Reporte Estudiantes Nuevo Ingreso', path: '/reporte-estudiantes-nuevo-ingreso', icon: '🆕', long: true },
            { label: 'Estimación de Alumnado', path: '/reporte-estimacion-alumnado', icon: '📊', long: true },
          ],
        },
      ],
    },
    {
      key: 'perfilAcademico',
      title: 'PERFIL ACADÉMICO',
      permiso: 'Perfil Académico',
      items: [
        { label: 'Grupos', path: '/grupos', icon: '👨‍👦‍👦', long: false },
        { label: 'Asesores', path: '/docentes', icon: '👨‍🏫', long: false },
        { label: 'Preasignación de Materias', path: '/preasignacion-materias', icon: '📚', long: true },
        { label: 'Asignación de Grupos', path: '/asignacion-grupos', icon: '👥', long: true },
      ],
      subgroups: [
        {
          key: 'reportesPerfilAcademico',
          title: 'Reportes',
          permiso: 'Perfil Académico',
          items: [
            { label: 'Reporte de Captura de Calificaciones', path: '/reporte-calificaciones', icon: '📈', long: true },
            { label: 'Reporte de Carga Académica', path: '/reporte-carga-academica', icon: '📚', long: true },
            { label: 'Reporte Estudiantes por Carrera', path: '/reporte-estudiantes-carrera', icon: '🎓', long: true },
          ],
        },
      ],
    },
    {
      key: 'adminSistema',
      title: 'ADMINISTRACIÓN DEL SISTEMA',
      permiso: 'Administración del Sistema',
      items: [
        { label: 'Gestión de Usuarios', path: '/gestion-usuarios', icon: '👥', long: true },
        { label: 'Roles y Permisos', path: '/roles-permisos', icon: '🔐', long: true },
      ],
      subgroups: [
        {
          key: 'catalogos',
          title: 'Catálogos',
          permiso: 'Administración del Sistema',
          items: [
            { label: 'Instituciones', path: '/instituciones', icon: '🏛️', long: false },
            { label: 'Campus', path: '/campus', icon: '🏫', long: false },
            { label: 'Secciones Académicas', path: '/secciones-academicas', icon: '📚', long: true },
            { label: 'Periodos Académicos', path: '/periodos', icon: '📅', long: true },
            { label: 'Ciclos Escolares', path: '/ciclos-escolares', icon: '🔄', long: true },
            { label: 'Conceptos de Cobro', path: '/conceptos-cobro', icon: '💰', long: true },
            { label: 'Becas y Apoyo', path: '/becas', icon: '🎯', long: true },
            { label: 'Academias', path: '/academias', icon: '🎓', long: false },
          ],
        },
        {
          key: 'reportes',
          title: 'Reportes',
          permiso: 'Administración del Sistema',
          items: [
            { label: 'Auditoría del Sistema', path: '/reporte-auditoria', icon: '📋', long: true },
          ],
        },
      ],
    },
    {
      key: 'admisiones',
      title: 'ADMISIONES',
      permiso: 'Admisiones',
      items: [
        { label: 'Gestión de Interesados', path: '/gestion-interesados', icon: '📋', long: true },
      ],
    },
    {
      key: 'panelAsesor',
      title: 'PANEL ASESOR',
      permiso: 'Panel Asesor',
      items: [
        // 'Registro de Calificaciones' se oculta del menú: la captura oficial ahora
        // se realiza desde Rúbrica. La ruta /registro-calificaciones se mantiene
        // activa para retrocompatibilidad con accesos directos y procesos internos.
        { label: 'Rúbrica', path: '/firma-concetrados', icon: '🖊️', long: true },
        { label: 'Registro de Asistencias', path: '/registro-asistencias', icon: '✓', long: true },
      ],
    },
  ];

  // Filtrar secciones basándose en los permisos del usuario
  const visibleSections = sections.filter(section => {
    // Si no vienen permisos, solo perfiles admin pueden ver todo.
    if (!user?.permisos || Object.keys(user.permisos).length === 0) {
      return isAdmin;
    }
    // Verificar si tiene permiso de lectura para el módulo
    return user.permisos[section.permiso]?.puede_leer === true;
  });

  const renderItem = (item) => {
    const active = item.path && isActive(item.path);
    const container = active ? styles.activeMenuItem : styles.menuItem;
    const textBase = item.long ? styles.menuTextLong : styles.menuText;
    const textCls = `${textBase} ${active ? styles.menuTextActive : ''}`;
    const iconWrapper = active ? styles.iconContainer : styles.iconContainerSecondary;
    const body = (
      <div className={container}>
        {active && <div className={styles.activeIndicator} />}
        <div className={styles.menuIcon}><div className={iconWrapper}><span>{item.icon}</span></div></div>
        {!isCollapsed && <span className={textCls}>{item.label}</span>}
      </div>
    );
    return item.path ? (
      <NavLink key={item.label} to={item.path} className={styles.menuItemLink} title={isCollapsed ? item.label : ''}>{body}</NavLink>
    ) : (
      <div key={item.label} className={styles.menuItemLink} style={{ cursor: 'default' }} title={isCollapsed ? item.label : ''}>{body}</div>
    );
  };

  return (
    <aside className={`${styles.sidebar} ${styles.sidebarFullHeight} ${isCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}
      style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {/* Header fijo con logo y texto */}
      <div className={logoStyles.logoHeader} style={{ flexShrink: 0 }}>
        <NavLink to="/inicio" className={styles.logoLink}>
          <div className={logoStyles.logoContainer}>
            <div className={logoStyles.logoMarginBottom}>
              <img src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/5e7b82052c97d59080050b9babf25376d2930ca3?placeholderIfAbsent=true" alt="Universidad Lux Logo" className={logoStyles.logo} />
            </div>
            {!isCollapsed && (
              <div className={logoStyles.logoText}>
                UNIVERSIDAD<span className={logoStyles.logoDivider}>|</span><span className={logoStyles.logoLux}>LUX</span>
              </div>
            )}
          </div>
        </NavLink>
        <div className={styles.toggleButtonContainer}>
          <button 
            className={styles.toggleButton}
            onClick={toggleSidebar}
            title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            aria-label={isCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
          >
            <span className={styles.toggleButtonIcon}>
              {isCollapsed ? (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 2L13 9L5 16" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 16L5 9L13 2" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
          </button>
        </div>
      </div>
      {/* Área scrollable solo para el menú */}
      <div className={logoStyles.sidebarMenuScrollable} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <nav className={styles.navigation}>
          {!isCollapsed && (
            <div className={styles.expandCollapseAllWrapper}>
              <button
                onClick={handleToggleAll}
                title={allExpanded ? 'Colapsar todo' : 'Expandir todo'}
                className={styles.expandCollapseAllButton}
              >
                <span className={styles.expandCollapseAllIcon + (allExpanded ? ' ' + styles.expanded : '')}>
                  {allExpanded ? (
                    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 9H14" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 8.5H14M9 4V13" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </span>
              </button>
            </div>
          )}
          {visibleSections.map((s, idx) => (
            <React.Fragment key={s.key}>
              <div 
                className={styles.sectionHeader + ' ' + (!isCollapsed ? styles.sectionHeaderPointer : '')}
                onClick={() => !isCollapsed && toggle(s.key)}
                title={isCollapsed ? s.title : ''}
              >
                <span className={styles.sectionTitle}>{isCollapsed ? s.title.charAt(0) : s.title}</span>
                {!isCollapsed && (
                  <span
                    className={styles.sectionArrow}
                    style={{ transform: `rotate(${open[s.key] ? '0deg' : '-90deg'})` }}
                  >▾</span>
                )}
              </div>
              {/* Submenú */}
              {!isCollapsed && (
                <div className={`${styles.menuGroup} ${open[s.key] ? styles.menuGroupExpanded : styles.menuGroupCollapsed}`}>
                  {open[s.key] && s.items.map(renderItem)}
                  {open[s.key] && s.subgroups && s.subgroups.map((subgroup) => (
                    <React.Fragment key={subgroup.key}>
                      <div 
                        className={styles.subgroupHeader}
                        onClick={() => toggle(subgroup.key)}
                        style={{ cursor: 'pointer', paddingLeft: '12px', marginTop: '8px' }}
                      >
                        <span className={styles.subgroupTitle} style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                          {subgroup.title}
                        </span>
                        <span
                          className={styles.sectionArrow}
                          style={{ transform: `rotate(${open[subgroup.key] ? '0deg' : '-90deg'})`, fontSize: '10px' }}
                        >▾</span>
                      </div>
                      <div className={`${styles.menuGroup} ${open[subgroup.key] ? styles.menuGroupExpanded : styles.menuGroupCollapsed}`} style={{ paddingLeft: '8px' }}>
                        {open[subgroup.key] && subgroup.items.map(renderItem)}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>
    </aside>
  );
}

export const SIDEBAR_WIDTH = 260;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

export { SidebarProvider };

export default Sidebar;
